import { supabase } from "./supabase";
import { computeHash } from "../utils/hashUtils";
import { logError } from "../utils/telemetry";

interface GenerateContentOptions {
  model: string;
  contents: any[];
  config?: any;
  systemInstruction?: any;
  signal?: AbortSignal;
  skipCache?: boolean;
}

const getCachedResponse = async (promptHash: string) => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const { data, error } = await supabase
    .from('gemini_cache')
    .select('response')
    .eq('prompt_hash', promptHash)
    .gt('created_at', thirtyDaysAgo.toISOString())
    .maybeSingle();

  if (error || !data) return null;
  return data.response;
};

export async function generateContentWithCache(options: GenerateContentOptions) {
  const modelName = (options.model && options.model.includes('gemini-3.5-flash-lite')) ? options.model : 'gemini-3.5-flash-lite';

  if (import.meta.env.VITE_MOCK_GEMINI === 'true') {
    return {
      text: '```json\n{"updatedData": {"materialCost": 800, "services": [{"name": "Mão de Obra", "value": 1200}], "timeHours": 56}, "isComplete": true, "reply": "Perfeito!", "explanation": "Mocked."}\n```',
      fromCache: false
    };
  }

  // 1. Generate unique hash for this specific request
  const cacheKey = await computeHash(JSON.stringify({
    model: modelName,
    contents: options.contents,
    config: options.config,
    systemInstruction: options.systemInstruction
  }));

  try {
    // 2. Check Supabase Cache (Skip if requested or Hit with 30-day TTL)
    if (!options.skipCache) {
      const cachedResponse = await getCachedResponse(cacheKey);

      if (cachedResponse) {
        if (import.meta.env.DEV) {
          console.log('Cache status: HIT');
        }
        return {
          text: cachedResponse,
          fromCache: true
        };
      }
    } else if (import.meta.env.DEV) {
      console.log('Cache status: BYPASSED');
    }

    // 3. Cache Miss: Call Gemini API
    if (import.meta.env.DEV) {
      console.log('Cache status: MISS');
    }
    
    let responseText = '';
    let retryCount = 0;
    const maxRetries = 2;
    
    const callGeminiBackend = async (modelName: string): Promise<any> => {
      let backendUrl = import.meta.env.VITE_BACKEND_URL || '';
      if (backendUrl.includes('run.app')) {
        backendUrl = '';
      }
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            headers['Authorization'] = `Bearer ${session.access_token}`;
          }
        } catch (e) {
          // Ignora falhas na sessão para fallback do Modo Visitante
        }

        const response = await fetch(`${backendUrl}/api/ai/generate`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            model: modelName,
            contents: options.contents,
            config: options.config,
            systemInstruction: options.systemInstruction,
          }),
          signal: options.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          if (response.status === 413 || errorText.includes('PayloadTooLargeError') || errorText.includes('entity too large')) {
            throw new Error('O arquivo enviado ultrapassa o limite de tamanho do servidor. Por favor, envie uma foto mais aproximada ou um arquivo menor.');
          }
          if (errorText.includes('429') || errorText.includes('depleted') || errorText.includes('quota') || errorText.includes('RESOURCE_EXHAUSTED')) {
            throw new Error('O leitor por foto está em manutenção rápida no momento. Escreva o que você precisa orçar no chat que calculamos para você!');
          }

          let cleanMsg = '';
          try {
            const parsed = JSON.parse(errorText);
            const rawErr = parsed.error;
            if (typeof rawErr === 'string') {
              const inner = JSON.parse(rawErr);
              cleanMsg = inner?.error?.message || inner?.message || rawErr;
            } else if (rawErr?.message) {
              cleanMsg = rawErr.message;
            } else if (parsed.message) {
              cleanMsg = parsed.message;
            }
          } catch {
            cleanMsg = errorText.replace(/<[^>]*>/g, ' ').substring(0, 120).trim();
          }

          if (cleanMsg.includes('depleted') || cleanMsg.includes('quota') || cleanMsg.includes('429')) {
            throw new Error('O leitor por foto está em manutenção rápida no momento. Escreva o que você precisa orçar no chat que calculamos para você!');
          }

          throw new Error(cleanMsg ? cleanMsg : 'Serviço em manutenção temporária. Por favor, escreva os detalhes do seu orçamento no chat!');
        }


        return await response.json();
      } catch (e: any) {
        const isRetryable = e?.message?.includes('502') || e?.message?.includes('503') || e?.message?.includes('504') || e?.message?.includes('Bad gateway') || e?.message?.includes('Failed to fetch') || e?.message?.includes('Network Error');
        if (isRetryable && retryCount < maxRetries) {
          retryCount++;
          const delay = Math.min(Math.pow(2, retryCount) * 1000, 8000); // cap at 8s
          console.warn(`AI request failed (${e?.message}). Retrying in ${delay}ms... (Attempt ${retryCount})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return callGeminiBackend(modelName);
        }
        throw e;
      }
    };

    try {
      const result = await callGeminiBackend(options.model);
      responseText = result.text;
    } catch (e: any) {
      throw e;
    }

    // 4. Save to Cache (Async, don't block return)
    supabase
      .from('gemini_cache')
      .upsert({
        prompt_hash: cacheKey,
        response: responseText
      }, { onConflict: 'prompt_hash' })
      .then(({ error: insertError }) => {
        if (insertError) console.error('Gemini Cache: Insert error', insertError);
      });

    return {
      text: responseText,
      fromCache: false
    };
  } catch (error) {
    console.error('Gemini Service Error:', error);
    logError(error, 'GeminiService', {
      model: options.model,
      contentsSnippet: options.contents ? JSON.stringify(options.contents).substring(0, 500) : null
    });
    throw error;
  }
}
